import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useDispatch } from "react-redux";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Layers,
  Loader2,
  Plus,
  Sparkles,
  X,
} from "lucide-react";
import { useCategories } from "../../contexts/CategoriesContext";
import { addConsumerTopicInterest, getErrorMessage } from "../../api/userAuthApi";
import { addUserInterest, setUser } from "../../store/slices/userSlice";
import showToast from "../../utils/toast";

function interestKey(categoryId, subCategoryId) {
  return `${categoryId}:${subCategoryId}`;
}

function formatInterestLabel(row) {
  const category =
    row?.category?.name || row?.category_name || row?.category || "Category";
  const sub =
    row?.sub_category?.name || row?.sub_category_name || row?.sub_category || "Topic";
  return { category, sub };
}

function userHasInterestId(user, categoryId, subCategoryId) {
  if (!user?.interests?.length) return false;
  return user.interests.some(
    (row) =>
      String(row?.category_id) === String(categoryId) &&
      String(row?.sub_category_id) === String(subCategoryId),
  );
}

export default function ManageInterestsSection({ user }) {
  const dispatch = useDispatch();
  const { signupCategories: categories, loading: catsLoading } = useCategories();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [expandedByCategory, setExpandedByCategory] = useState({});
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState(null);

  const existingKeys = useMemo(() => {
    const keys = new Set();
    (user?.interests ?? []).forEach((row) => {
      if (row?.category_id != null && row?.sub_category_id != null) {
        keys.add(interestKey(row.category_id, row.sub_category_id));
      }
    });
    return keys;
  }, [user?.interests]);

  const interestsList = useMemo(
    () => (Array.isArray(user?.interests) ? user.interests : []),
    [user?.interests],
  );

  useEffect(() => {
    if (!pickerOpen || catsLoading || !categories.length) return;
    const first = categories.find((c) => (c.sub_categories ?? []).length > 0);
    if (!first) return;
    setExpandedByCategory((prev) => ({ ...prev, [first.name]: true }));
  }, [pickerOpen, catsLoading, categories]);

  const selectDraft = (cat, sub) => {
    const key = interestKey(cat.id, sub.id);
    if (existingKeys.has(key)) return;
    const isSame =
      draft?.categoryId === cat.id && draft?.subCategoryId === sub.id;
    setDraft(isSame ? null : {
      categoryId: cat.id,
      subCategoryId: sub.id,
      categoryName: cat.name,
      subCategoryName: sub.name,
    });
  };

  const handleAddInterest = async () => {
    if (!draft || pending) return;
    const uid = user?.id ?? user?.user_id;
    if (uid == null) {
      showToast.error("Could not read your account. Please sign in again.");
      return;
    }

    setPending(true);
    try {
      const result = await addConsumerTopicInterest({
        user_id: uid,
        category: draft.categoryName,
        sub_category: draft.subCategoryName,
      });

      if (result?.user && typeof result.user === "object" && !Array.isArray(result.user)) {
        dispatch(setUser(result.user));
      } else if (result?._stub) {
        dispatch(
          addUserInterest({
            id: `local-${Date.now()}`,
            user_id: uid,
            category_id: draft.categoryId,
            sub_category_id: draft.subCategoryId,
            category: {
              id: draft.categoryId,
              name: draft.categoryName,
            },
            sub_category: {
              id: draft.subCategoryId,
              name: draft.subCategoryName,
              category_id: draft.categoryId,
            },
          }),
        );
      }

      const toastMsg =
        typeof result?.reason === "string" && result.reason.trim()
          ? result.reason.trim()
          : "Interest added — we'll tailor content for this topic.";
      showToast.success(toastMsg);
      setDraft(null);
      setPickerOpen(false);
    } catch (err) {
      showToast.error(getErrorMessage(err) || "Could not add this topic. Try again.");
    } finally {
      setPending(false);
    }
  };

  const drawer =
    typeof document !== "undefined"
      ? createPortal(
          <AnimatePresence mode="sync">
            {pickerOpen ? (
              <motion.div
                key="profile-interests-shell"
                role="presentation"
                className="fixed inset-0 z-[200] flex justify-end"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <button
                  type="button"
                  aria-label="Close topic browser"
                  className="absolute inset-0 bg-slate-950/[0.45] backdrop-blur-[3px]"
                  onClick={() => !pending && setPickerOpen(false)}
                />
                <motion.aside
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="profile-interests-title"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", damping: 32, stiffness: 340 }}
                  className="relative z-10 flex h-[100dvh] w-full max-w-md flex-col rounded-l-2xl bg-white shadow-[-12px_0_48px_-12px_rgba(15,23,42,0.28)]"
                >
                  <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#FFD221] via-amber-300 to-[#FFD221]" />
                  <div className="shrink-0 border-b border-slate-200/90 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 px-5 pb-4 pt-[max(1rem,env(safe-area-inset-top))] text-white">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#FFD221]/90">
                          Interests
                        </p>
                        <h2 id="profile-interests-title" className="mt-1 text-lg font-black">
                          Add a topic
                        </h2>
                        <p className="mt-1.5 text-xs text-white/65">
                          Pick one sub-topic you don&apos;t already follow. You can add more anytime.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => !pending && setPickerOpen(false)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white"
                        aria-label="Close"
                      >
                        <X size={20} />
                      </button>
                    </div>
                    <div
                      className={[
                        "mt-4 flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-semibold",
                        draft
                          ? "border-emerald-400/30 bg-emerald-500/15 text-emerald-50"
                          : "border-amber-400/25 bg-amber-500/10 text-amber-50/95",
                      ].join(" ")}
                    >
                      {draft ? (
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" />
                      ) : (
                        <AlertCircle className="h-5 w-5 shrink-0 text-amber-300" />
                      )}
                      <span>
                        {draft
                          ? `${draft.categoryName} · ${draft.subCategoryName}`
                          : "Select one new sub-topic below"}
                      </span>
                    </div>
                  </div>

                  <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3">
                    {catsLoading ? (
                      <div className="flex flex-col items-center gap-3 py-16 text-slate-400">
                        <Loader2 className="h-8 w-8 animate-spin text-[#9a7b00]" />
                        <p className="text-sm font-medium">Loading topic areas…</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {categories.map((cat, catIndex) => {
                          const subs = cat.sub_categories ?? [];
                          const expanded = Boolean(expandedByCategory[cat.name]);
                          const panelId = `profile-int-acc-${catIndex}`;
                          return (
                            <section
                              key={cat.name}
                              className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm"
                            >
                              <button
                                type="button"
                                disabled={!subs.length || pending}
                                onClick={() =>
                                  setExpandedByCategory((prev) => ({
                                    ...prev,
                                    [cat.name]: !prev[cat.name],
                                  }))
                                }
                                className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50/95"
                                aria-expanded={expanded}
                                aria-controls={panelId}
                              >
                                <ChevronDown
                                  size={18}
                                  className={`shrink-0 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                                />
                                <span className="flex-1 text-sm font-bold text-slate-900">{cat.name}</span>
                              </button>
                              {expanded && subs.length > 0 ? (
                                <div id={panelId} className="border-t border-slate-100 bg-slate-50/70 px-2 py-2">
                                  <ul className="space-y-1">
                                    {subs.map((sub) => {
                                      const owned = userHasInterestId(user, cat.id, sub.id);
                                      const picked =
                                        draft?.categoryId === cat.id &&
                                        draft?.subCategoryId === sub.id;
                                      return (
                                        <li key={`${cat.id}-${sub.id}`}>
                                          <label
                                            className={[
                                              "flex items-start gap-3 rounded-xl px-2 py-2",
                                              owned
                                                ? "cursor-not-allowed opacity-50"
                                                : "cursor-pointer",
                                              picked
                                                ? "bg-[#FFD221]/12 ring-1 ring-[#FFD221]/35"
                                                : "hover:bg-white/80",
                                            ].join(" ")}
                                          >
                                            <input
                                              type="radio"
                                              name="profile-interest-sub"
                                              className="mt-0.5 h-4 w-4 accent-[#FFD221]"
                                              checked={picked}
                                              disabled={owned || pending}
                                              onChange={() => selectDraft(cat, sub)}
                                            />
                                            <span className="min-w-0 text-sm text-slate-700">
                                              {sub.name}
                                              {owned ? (
                                                <span className="ml-1 text-[11px] font-semibold text-emerald-700">
                                                  (added)
                                                </span>
                                              ) : null}
                                            </span>
                                          </label>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              ) : null}
                            </section>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 border-t border-slate-200 bg-white px-5 py-4">
                    <button
                      type="button"
                      disabled={!draft || pending}
                      onClick={handleAddInterest}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {pending ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Adding…
                        </>
                      ) : (
                        <>Add to my interests</>
                      )}
                    </button>
                  </div>
                </motion.aside>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.body,
        )
      : null;

  return (
    <>
      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-slate-800 to-slate-950 text-[#FFD221]">
              <Layers className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Content interests</h2>
              <p className="mt-1 text-sm text-slate-500">
                Topics you follow unlock articles, media, and tailored updates on subcategory pages.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-black text-black shadow-sm"
            style={{ background: "#FFD221" }}
          >
            <Plus size={16} />
            Add interest
          </button>
        </div>

        {interestsList.length > 0 ? (
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {interestsList.map((row) => {
              const { category, sub } = formatInterestLabel(row);
              const key =
                row?.id ??
                interestKey(row?.category_id, row?.sub_category_id) ??
                `${category}-${sub}`;
              return (
                <div
                  key={key}
                  className="rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-2.5"
                >
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                    {category}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{sub}</p>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mt-5 flex items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
            <Sparkles className="h-4 w-4 text-[#9a7b00]" />
            No interests yet — add a topic to unlock content across Infoboxx.
          </p>
        )}
      </div>
      {drawer}
    </>
  );
}
